import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";

import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import Table from "themeComponents/Tables/Table";
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "themeComponents/Navbars/DashboardNavbar";
import Footer from "themeComponents/Footer";
import Loader from "components/loader";
import EmptyStateCard from "layouts/courses/components/EmptyStateCard";
import useAuth from "hooks/useAuth";
import { userApi } from "configs/api";

interface CreatorRow {
  _id: string;
  name?: string;
  firstname?: string;
  lastname?: string;
  email?: string;
  role?: string;
  telephone1?: string;
  companyName?: string;
  status?: boolean;
  createdAt?: string;
}

function Creators(): React.JSX.Element {
  const { isAdmin } = useAuth();
  const [page, setPage] = useState(1);
  const limit = 24;
  const [rows, setRows] = useState<CreatorRow[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const res = await userApi.get("/api/users", {
        params: { role: "creator", page, limit },
      });
      const ok = res.data?.status === true;
      const payload = res.data?.data;
      const list: CreatorRow[] = (ok && Array.isArray(payload?.data) ? payload.data : []) as CreatorRow[];
      const tp = ok && typeof payload?.totalPages === "number" ? payload.totalPages : 1;
      setRows(list);
      setTotalPages(Math.max(1, tp));
    } catch {
      setRows([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, limit]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <Box mb={3}>
          <Typography variant="h4" fontWeight="bold" mb={1}>
            Creators
          </Typography>
          <Typography variant="body2" color="text">
            All platform users with the creator role (super admin only).
          </Typography>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8} {...({} as any)}>
            <Loader loading={true} {...({} as any)} />
          </Box>
        ) : rows.length === 0 ? (
          <EmptyStateCard title="No creators found" />
        ) : (
          <Card>
            <Box
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }: any) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              <Table
                columns={[
                  { name: "name", align: "left" },
                  { name: "email", align: "left" },
                  { name: "phone", align: "left" },
                  { name: "company", align: "left" },
                  { name: "status", align: "center" },
                ]}
                rows={rows.map((u) => ({
                  name: (
                    <Typography variant="caption" fontWeight="medium">
                      {u.name || `${u.firstname || ""} ${u.lastname || ""}`.trim() || "—"}
                    </Typography>
                  ),
                  email: (
                    <Typography variant="caption" color="text">
                      {u.email || "—"}
                    </Typography>
                  ),
                  phone: (
                    <Typography variant="caption" color="text">
                      {u.telephone1 || "—"}
                    </Typography>
                  ),
                  company: (
                    <Typography variant="caption" color="text">
                      {u.companyName || "—"}
                    </Typography>
                  ),
                  status: (
                    <Typography variant="caption" fontWeight="medium" color={u.status === false ? "error" : "success"}>
                      {u.status === false ? "Inactive" : "Active"}
                    </Typography>
                  ),
                }))}
              />
            </Box>
            {totalPages > 1 && (
              <CardContent>
                <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                  <Button variant="outlined" color="info" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Typography variant="body2">
                    Page {page} of {totalPages}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="info"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </Box>
              </CardContent>
            )}
          </Card>
        )}
      </Box>
      <Footer />
    </DashboardLayout>
  );
}

export default Creators;
